import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { Spinner } from 'react-bootstrap';
import { downloadFile, delayMethod } from '../../../services/utils';
import { DefaultModal, DocumentsTabContainer, SimpleCenteredModal, WorkflowTimeline } from '../../../components';
import { DirectoryNodesProvider } from '../../../components/DocumentsTabContainer/DirectoryNodesContext';

import {
  deleteDirectoryFileRequest,
  deleteDirectoryNodeRequest,
  downloadDirectoryNodeRequest,
  updateFolderRequest,
  exportDirectoryNodeRequest,
  indexDirectoryNodesRequest,
  childrenDirectoryNodeRequest,
  documentsDirectoryNodeRequest
} from '../../../requests/directoryNodes';
import { updateDocumentRequest } from '../../../requests/documents';
import { sendAlert } from '../../../actions/utils';
import DocumentPreview from '../../DocumentPreview/DocumentPreview';
import findNodeRecursive, {
  updateNodeRecursively,
  closeParentAndChildren,
  openFolderAndCloseSiblings,
  findDocumentRecursive,
  findChildrenWithParent
} from './helpers';

const DirectoryNodeBoxes = ({
  employeeId,
  moreDataCenterModal,
  handleOpenCenterModal,
  handleOpenImportModal,
  handleEditModal,
  rrhh = false
}) => {
  const [directoryNodes, setDirectoryNodes] = useState([]);
  const [modalAction, setModalAction] = useState('');
  const [modalBody, setModalBody] = useState('');
  const [modalItem, setModalItem] = useState({});
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSize, setModalSize] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documentModalShow, setDocumentModalShow] = useState(false);
  const [documentModalBody, setDocumentModalBody] = useState('');
  const [onRequest, setOnRequest] = useState(true);
  const dispatch = useDispatch();
  const guideLines = {
    work_contract: ['approved'],
    contract_annex: ['approved'],
    salary_settlement: ['approved'],
    work_settlement: ['approved'],
    reprimand_document: ['approved'],
    congratulation_letter: ['approved'],
    salary_certificate: ['approved']
  };

  const addFirstIcons = directoryNodesResponse => {
    const folders = directoryNodesResponse;
    folders.forEach((folder, i) => {
      switch (folder.documentType) {
        case 'work_contract':
          folders[i].icon = 'document';
          break;
        case 'certificate':
          folders[i].icon = 'school';
          break;
        case 'contract_annex':
          folders[i].icon = 'document-attach';
          break;
        case 'work_settlement':
          folders[i].icon = 'exit';
          break;
        case 'salary_settlement':
          folders[i].icon = 'barcode';
          break;
        case 'employee_requests':
          folders[i].icon = 'document-text';
          break;
        case 'other_documents':
          folders[i].icon = 'grid';
          break;
        case 'reprimand_document':
          folders[i].icon = 'warning';
          break;
        default:
          break;
      }
    });
    return folders;
  };

  const handleSuccessIndex = response => {
    setDirectoryNodes(addFirstIcons(camelCaseRecursive(response.data.data)));
  };

  const handleSuccessRequest = message => {
    dispatch(sendAlert({ kind: 'success', message }));
  };

  const handleFailureRequest = error => {
    const { response } = error;

    let errorMessage = response.statusText;
    if (response.data && response.data.message) {
      errorMessage = response.data.message;
    }

    if (response.data.message.indexOf('trabajador') > 0) {
      setModalTitle(
        <span role="img" aria-label="">
          {' '}
          ¡UPS! &#128559;{' '}
        </span>
      );
      setModalBody(errorMessage);
      setModalAction('close');
      setModalShow(true);
    }
  };

  const defaultCallback = () => {
    setModalShow(false);
    setOnRequest(false);
  };

  const handleRequest = () => {
    setOnRequest(true);

    const params = {
      tree_data: '100',
      light_hierarchy: true
    };

    if (rrhh === false) {
      params.documents_filter_guidelines = JSON.stringify(guideLines);
    }

    const request = async () =>
      indexDirectoryNodesRequest({
        employeeId,
        dispatch,
        params,
        successCallback: handleSuccessIndex,
        callback: () => setOnRequest(false)
      });
    request();
  };

  const handleFirstNodeRequest = async (directoryNodeId, setFolder = false) => {
    setDirectoryNodes(oldDirectoryNodes => {
      const updatePayload = {
        isLoading: true
      };
      return updateNodeRecursively(oldDirectoryNodes, directoryNodeId, updatePayload);
    });
    const folderIsOpen = { isOpen: false };
    const params = {
      employee_id: employeeId,
      sort_column: 'created_at',
      sort_direction: 'desc'
    };
    if (rrhh === false) {
      params.documents_filter_guidelines = JSON.stringify(guideLines);
    }
    const childrenPromise = new Promise((resolve, reject) => {
      childrenDirectoryNodeRequest(directoryNodeId, {
        params,
        dispatch,
        successCallback: ({ data }) => resolve({ data }),
        callback: reject
      });
    });

    const documentsPromise = new Promise((resolve, reject) => {
      documentsDirectoryNodeRequest(directoryNodeId, {
        params,
        dispatch,
        successCallback: ({ data }) => resolve({ data }),
        callback: reject
      });
    });
    const result = await Promise.all([childrenPromise, documentsPromise]);

    if (setFolder) {
      setSelectedFolder(directoryNodeId);
      folderIsOpen.isOpen = true;
    }

    setDirectoryNodes(oldDirectoryNodes => {
      const childrenData = camelCaseRecursive(result.at(0)?.data);
      const documentsData = camelCaseRecursive(result.at(1)?.data);
      const updatePayload = {
        children: childrenData,
        hasChildren: childrenData?.length > 0,
        documents: documentsData?.documents || [],
        nextPage: documentsData?.nextPage || false,
        isLoading: false,
        isLoaded: true,
        isOpen: folderIsOpen.isOpen
      };
      return updateNodeRecursively(oldDirectoryNodes, directoryNodeId, updatePayload);
    });
  };

  const handleLoadMoreDocuments = (directoryNodeId, nextPage, setIsLoading) => {
    const params = {
      employee_id: employeeId,
      sort_column: 'created_at',
      sort_direction: 'desc',
      page: nextPage
    };
    if (rrhh === false) {
      params.documents_filter_guidelines = JSON.stringify(guideLines);
    }
    documentsDirectoryNodeRequest(directoryNodeId, {
      params,
      dispatch,
      successCallback: ({ data }) => {
        setIsLoading(false);
        if (data?.documents?.length === 0) {
          return;
        }
        setDirectoryNodes(oldDirectoryNodes => {
          const parsedData = camelCaseRecursive(data?.documents || []);
          const oldDocuments = findNodeRecursive(oldDirectoryNodes, directoryNodeId)?.documents || [];
          const documentsData = [...oldDocuments, ...parsedData];
          return updateNodeRecursively(oldDirectoryNodes, directoryNodeId, {
            documents: documentsData,
            nextPage: data?.next_page || false,
            isLoaded: true
          });
        });
      },
      callback: () => setIsLoading(false)
    });
  };

  useEffect(handleRequest, [moreDataCenterModal]);

  const removeDocumentFromCurrentNode = documentId => {
    setDirectoryNodes(oldDirectoryNodes => {
      const nodeData = findDocumentRecursive(oldDirectoryNodes, documentId);
      const nodeId = nodeData?.id;
      const newProp = nodeData?.documents?.filter(element => element.id !== documentId);
      return updateNodeRecursively(oldDirectoryNodes, nodeId, {
        documents: newProp
      });
    });
  };

  const removeChildrenFromCurrentNode = childrenId => {
    setDirectoryNodes(oldDirectoryNodes => {
      const parentNode = findChildrenWithParent(oldDirectoryNodes, childrenId);
      if (!parentNode) return oldDirectoryNodes;
      const newChildren = parentNode?.children?.filter(element => element.id !== childrenId);
      return updateNodeRecursively(oldDirectoryNodes, parentNode?.id, {
        children: newChildren
      });
    });
  };

  const handleMoveDocument = (directoryNodeId, documentId) => {
    setOnRequest(true);

    updateDocumentRequest(documentId, {
      employeeId,
      dispatch,
      params: {
        directory_node_id: directoryNodeId
      },
      successCallback: () => handleSuccessRequest('Archivo movido con éxito'),
      callback: () => {
        removeDocumentFromCurrentNode(documentId);
        handleFirstNodeRequest(directoryNodeId);
        defaultCallback();
      }
    });
  };

  const handleMoveFolder = (directoryNodeId, folderId) => {
    setOnRequest(true);

    updateFolderRequest(folderId, {
      employeeId,
      dispatch,
      params: {
        parent_id: directoryNodeId
      },
      successCallback: () => handleSuccessRequest('Carpeta movida con éxito'),
      callback: () => {
        removeChildrenFromCurrentNode(folderId);
        handleFirstNodeRequest(directoryNodeId);
        defaultCallback();
      }
    });
  };

  const removeDirectoryFolder = directoryFolder => {
    const [folderId, directoryNodeId] = directoryFolder;
    delayMethod(() => setModalShow(false), 160);
    setOnRequest(true);

    deleteDirectoryNodeRequest(folderId, {
      employeeId,
      dispatch,
      successCallback: () => handleSuccessRequest('Carpeta eliminada con éxito'),
      callback: () => {
        handleFirstNodeRequest(directoryNodeId, true);
        defaultCallback();
      }
    });
  };

  const removeDirectoryFile = directoryFile => {
    delayMethod(() => setModalShow(false), 160);
    setOnRequest(true);

    const [document, folderId] = directoryFile;
    deleteDirectoryFileRequest({
      params: {
        documents: [
          {
            directory_node_id: folderId,
            document_id: document.id,
            employee_id: employeeId
          }
        ]
      },
      dispatch,
      successCallback: () => handleSuccessRequest('Archivo eliminado con éxito'),
      failureCallback: error => handleFailureRequest(error),
      callback: () => {
        setOnRequest(false);
        handleFirstNodeRequest(folderId);
      }
    });
  };

  const downloadDirectoryFolder = directoryFolder => {
    downloadDirectoryNodeRequest(directoryFolder.id, {
      employeeId,
      dispatch,
      successCallback: downloadFile,
      callback: defaultCallback
    });
  };

  const exportDirectoryNode = directoryNode => {
    exportDirectoryNodeRequest(directoryNode.id, {
      employeeId,
      dispatch,
      successCallback: downloadFile,
      callback: defaultCallback
    });
  };

  const handleButtonClick = (item, action, parentId) => {
    switch (action) {
      case 'show':
        if (item.documentType === 'risk_prevention') {
          window.open(item.fileInfo?.fileUrl, '_blank');
        } else {
          setDocumentModalBody(<DocumentPreview documentId={item.id} employeeId={item.employeeId} />);
          setDocumentModalShow(true);
        }
        break;
      case 'download':
        window.open(item.fileInfo.fileUrl, '_blank');
        break;
      case 'remove':
        setModalAction('remove');
        setModalShow(true);
        setModalItem([item, parentId]);
        setModalTitle('Eliminar Archivo');
        setModalBody(`¿Estás seguro que deseas eliminar ${item.fileInfo.filename}?`);
        break;
      case 'download-folder':
        setModalAction('download-folder');
        setModalShow(true);
        setModalItem(item);
        setModalTitle('Exportar Plantilla de Actualización');
        setModalBody(`¿Estás seguro que deseas descargar la plantilla de actualización para la carpeta ${item.name}?`);
        break;
      case 'export-folder':
        setModalAction('export-folder');
        setModalShow(true);
        setModalItem(item);
        setModalTitle('Exportar carpeta');
        setModalBody(`¿Estás seguro que deseas exportar la carpeta ${item.name}?`);
        break;
      case 'remove-folder':
        setModalAction('remove-folder');
        setModalShow(true);
        setModalItem([item.id, parentId]);
        setModalTitle('Eliminar carpeta');
        setModalBody(`¿Estás seguro que deseas eliminar ${item.name}?`);
        break;
      case 'import-folder':
        handleOpenImportModal(item);
        break;
      case 'change-name':
        handleEditModal(item.id, item.name, setOnRequest, () => handleFirstNodeRequest(parentId));
        break;
      case 'show-workflow':
        setModalTitle('Detalles del flujo');
        setModalBody(
          <WorkflowTimeline
            workflowId={item.workflowRequestId}
            document={camelCaseRecursive(item)}
            handleClose={() => setModalShow(false)}
          />
        );
        setModalSize('lg');
        setModalShow(true);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Error: Action not found');
    }
  };

  const handleDocumentModalClose = () => {
    setDocumentModalShow(false);
  };

  const handleModalConfirm = () => {
    switch (modalAction) {
      case 'remove-folder':
        removeDirectoryFolder(modalItem);
        break;
      case 'remove':
        removeDirectoryFile(modalItem);
        break;
      case 'download-folder':
        downloadDirectoryFolder(modalItem);
        break;
      case 'export-folder':
        exportDirectoryNode(modalItem);
        break;
      case 'close':
        setModalShow(false);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Error: Action not found');
    }
  };

  const closeParentAndChildrenHandler = parentId => {
    setDirectoryNodes(prev => closeParentAndChildren(prev, parentId));
  };

  const openFolderAndCloseSiblingsHandler = folderId => {
    setDirectoryNodes(prev => openFolderAndCloseSiblings(prev, folderId));
  };

  const updateNode = (targetId, updatePayload) => {
    setDirectoryNodes(prev => updateNodeRecursively(prev, targetId, updatePayload));
  };

  return (
    <DirectoryNodesProvider
      directoryNodes={directoryNodes}
      setDirectoryNodes={setDirectoryNodes}
      closeParentAndChildren={closeParentAndChildrenHandler}
      openFolderAndCloseSiblings={openFolderAndCloseSiblingsHandler}
      setSelectedFolder={setSelectedFolder}
      selectedFolder={selectedFolder}
      updateNode={updateNode}
      rrhh={rrhh}
    >
      {onRequest && (
        <div className="container-documents-spinner">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      <div className={`${onRequest && 'bg-opacity'}`}>
        <DocumentsTabContainer
          handleFirstNodeRequest={handleFirstNodeRequest}
          handleLoadMoreDocuments={handleLoadMoreDocuments}
          handleOpenCenterModal={handleOpenCenterModal}
          handleButtonClick={handleButtonClick}
          handleMoveDocument={handleMoveDocument}
          handleMoveFolder={handleMoveFolder}
          permitedActions={['show', 'remove']}
          permitedCreations={['node', 'file']}
          maxDepth={100}
        />
        <DefaultModal
          title={modalTitle}
          body={modalBody}
          show={modalShow}
          modalSize={modalSize}
          titleBtnClose="Cancelar"
          handleClose={() => setModalShow(false)}
          titleBtnSave="Confirmar"
          handleConfirm={handleModalConfirm}
        />
        <SimpleCenteredModal
          body={documentModalBody}
          onHide={handleDocumentModalClose}
          show={documentModalShow}
          size="xl"
        />
      </div>
    </DirectoryNodesProvider>
  );
};

export default DirectoryNodeBoxes;
